import React, { useState } from 'react';
import { Input } from 'antd';
import { useFormikContext } from 'formik';
import { InputProps } from 'antd/lib/input';
import { FormikFieldProps } from 'formik-antd/lib/FieldProps';
import EyeTwoTone from '@ant-design/icons/EyeTwoTone';
import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';
import cx from 'classnames';

import 'text-security/text-security-disc.css';

import styles from './SecureInput.module.less';

type Props = InputProps & FormikFieldProps;

// Formik state should have the next structure
// status: {
//  ...anyOtherFields,
//  secureStatus: {
//    [fieldName]: {
//     firstChangeOccurred: Boolean,
//     plainTextVisible: Boolean,
//    }
//  }
// }

export default React.forwardRef((props: Props, ref: any) => {
  const { name, className } = props;
  const { setStatus, status } = useFormikContext();
  const initialSecureStatus = status?.secureStatus?.[name];
  const [firstChangeOccurred, setFirstChangeOccurred] = useState(
    initialSecureStatus?.firstChangeOccurred || false,
  );
  const [plainTextVisible, setPlainTextVisible] = useState(
    initialSecureStatus?.plainTextVisible || false,
  );

  const onVisibleChange = () => {
    if (props.disabled) {
      return;
    }

    setPlainTextVisible(!plainTextVisible);
    updateFormikState(firstChangeOccurred, !plainTextVisible);
  };

  const onInputChange = (event: any) => {
    setFirstChangeOccurred(true);

    if (!firstChangeOccurred) {
      updateFormikState(true, plainTextVisible);

      const { data, target } = event.nativeEvent;
      event.target.value = data || target?.clipboardText || ''; // clear field and set only entered/pasted value
      // modify only event.target.value
      // the real value will be updated only via 'value' prop
      // works only when the next props.onChange() will pass new updated 'value' prop to the input
    }

    props.onChange?.(event);
  };

  const updateFormikState = (
    firstChangeOccurred: boolean,
    plainTextVisible: boolean,
  ) => {
    setStatus({
      ...status,
      secureStatus: {
        ...status?.secureStatus,
        [name]: {
          firstChangeOccurred,
          plainTextVisible,
        },
      },
    });
  };

  const getIcon = () => {
    if (!firstChangeOccurred) return <span />; // https://ant.design/components/input/#Why-Input-lose-focus-when-change-prefix/suffix

    const iconProps = {
      onClick: onVisibleChange,
      onMouseDown: (e: any) => {
        e.preventDefault();
      },
      onMouseUp: (e: any) => {
        e.preventDefault();
      },
    };
    const Icon = plainTextVisible ? EyeTwoTone : EyeInvisibleOutlined;

    return <Icon {...iconProps} />;
  };

  const onPaste = (event: any) => {
    event.target.clipboardText = event.clipboardData.getData('text');
  };

  return (
    <Input
      {...props}
      name={name}
      ref={ref}
      className={cx(className, { [styles.secure]: !plainTextVisible })}
      onChange={onInputChange}
      onPaste={onPaste}
      suffix={getIcon()}
      autoComplete="off"
    />
  );
});
